// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ResetShippingAddressFormValidation";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import updateCheckoutShippingShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import validateShippingAddressForm from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ValidateShippingAddressForm";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { CountryModel, CustomerValidationDto, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BillingAddressInfoDisplayStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import ShippingAddressForm from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutBox from "@steiner/App/Components/CheckoutReviewAndSubmit/CheckoutBox";
import Notify from "@steiner/App/Components/Notify";
import BillingAddressInfoDisplay from "@steiner/Overrides/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import ShippingAddressInfoDisplay, {
    ShippingAddressInfoDisplayStyles,
} from "@steiner/Overrides/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import { StyledHr } from "@steiner/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    // XNG-Changes
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    const addressFieldsDataView = getAddressFieldsDataView(state);
    return {
        cartId,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        shippingPageNavLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        // XNG-Changes
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        shipTosDataView,
        shouldLoadAddressFields: !addressFieldsDataView.value && !addressFieldsDataView.isLoading,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    updateCart: makeHandlerChainAwaitable(updateCart),
    setUseOneTimeAddress,
    // XNG-Changes
    loadShipTos,
    loadAddressFields,
    validateShippingAddressForm,
    resetShippingAddressFormValidation,
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address,
        });
    },
    updateCheckoutShippingShipTo,
};

interface OwnProps {}

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

interface ShippingBillingInformationStyles {
    container?: InjectableCss;
    shippingAddress?: ShippingAddressInfoDisplayStyles;
    billingAddress?: BillingAddressInfoDisplayStyles;
    styledHr?: InjectableCss;
    changeShipToAddress?: InjectableCss;
    actionsContainer?: InjectableCss;
    orTitle?: TypographyPresentationProps;
    updateShipButton?: ButtonPresentationProps;
    cancelAddingOneTimeAddressButton?: ButtonPresentationProps;
    saveAddingOneTimeAddressButton?: ButtonPresentationProps;
    actionsButtonContainer?: InjectableCss;
    reviewShipToMessage?: InjectableCss;
}

export const shippingBillingInformationStyles: ShippingBillingInformationStyles = {};

const styles = shippingBillingInformationStyles;
const ShippingBillingInformation: FC<Props> = ({
    cart,
    shipToState,
    billToState,
    updateCart,
    // XNG-Changes
    countries,
    shipToAddressFields,
    setUseOneTimeAddress,
    useOneTimeAddress,
    shipTosDataView,
    loadShipTos,
    shouldLoadAddressFields,
    loadAddressFields,
    shippingAddressFormState,
    validateShippingAddressForm,
    resetShippingAddressFormValidation,
    currentUserIsGuest,
    setAddress,
    updateCheckoutShippingShipTo,
}) => {
    const [shippingId, setShippingId] = React.useState<string | undefined>(cart?.shipToId);
    const [useOneTimeAddressState, setUseOneTimeAddressState] = React.useState<boolean>(false);
    // XNG-Changes
    /**
     * STNRS-431
     * This is a workaround to allow select ship to one time address from the review and submit page.
     * If form was submitted we need to validate entered values live time
     */
    const [submitted, setSubmitted] = React.useState(false);
    const [hasErrors, setHasErrors] = React.useState(false);
    const [hasOpenBid, setHasOpenBid] = React.useState(false);

    useEffect(() => {
        // if it is one time address then we should select ship to one time address
        if (cart?.shipToLabel === "Ship to One-Time Address") {
            setShippingId(billToState?.value?.shipTos?.find(shipTo => shipTo.oneTimeAddress)?.id);
        } else {
            setShippingId(cart?.shipToId);
        }
    }, [cart]);

    useEffect(() => {
        if(cart?.cartLines?.length) {
            setHasOpenBid(cart.cartLines.some(function (r) { return r.properties["isBidItem"] === "true"; }));
        }
    }, [cart?.cartLines]);

    // XNG-Changes
    const shouldValidateAddress = (validation: CustomerValidationDto) => {
        return validation.firstName?.isDisabled === false ||
            validation.lastName?.isDisabled === false ||
            validation.companyName?.isDisabled === false ||
            validation.attention?.isDisabled === false ||
            validation.address1?.isDisabled === false ||
            validation.address2?.isDisabled === false ||
            validation.address3?.isDisabled === false ||
            validation.address4?.isDisabled === false ||
            validation.city?.isDisabled === false ||
            validation.state?.isDisabled === false ||
            validation.country?.isDisabled === false ||
            validation.postalCode?.isDisabled === false ||
            validation.phone?.isDisabled === false ||
            validation.email?.isDisabled === false ||
            validation.fax?.isDisabled === false;
    }
    useEffect(() => {
        if(submitted && hasErrors && shippingAddressFormState?.address.validation) {
            const address = shippingAddressFormState.address;
            const { validation } = shippingAddressFormState.address;
            
            if (shouldValidateAddress(validation) && shipToAddressFields) {
                validateShippingAddressForm({
                    address,
                    validation,
                    fieldDisplay: shipToAddressFields,
                    onSuccess: (result: any) => {
                        if(result) {
                            setHasErrors(false);
                        }
                    }
                });
            }
        }
    }, [shippingAddressFormState?.address, submitted, hasErrors]);

    /**
     * XNG-Changes
     * STNRS-431
     * This is a workaround to allow select ship to one time address from the review and submit page.
     * We need to make sure we load the ship tos for the bill to before we can show the one time address.
     * and load address fields for the one time address.
     */
    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
        if (shouldLoadAddressFields) {
            loadAddressFields();
        }
    });

    if (!shipToState.value || !billToState.value || !cart) {
        return null;
    }

    // XNG-Changes
    const shippingAddressChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setShippingId(event.target.value);
        updateCheckoutShippingShipTo({
            billToId: cart?.billToId || "",
            shipTo: billToState.value.shipTos?.find(shipTo => shipTo.id === event.target.value) as ShipToModel,
            isEdit: false,
            onSuccess: hasCartlines => {
                updateCart({ shipToId: event.target.value });
                const oneTimeAddresId = billToState.value.shipTos?.find(shipTo => shipTo.oneTimeAddress)?.id;
                setUseOneTimeAddressState(event.target.value === oneTimeAddresId);
            },
        });
      
    };

    const saveOneTimeAddress = () => {
        setSubmitted(true);
        if (!shippingAddressFormState?.address?.validation) {
            return;
        }
        const address = shippingAddressFormState.address;
        const { validation } = shippingAddressFormState.address;
        
        if (shouldValidateAddress(validation) && shipToAddressFields) {
            validateShippingAddressForm({
                address,
                validation,
                fieldDisplay: shipToAddressFields,
                onSuccess: (result: any) => {
                    if(result) {
                        setUseOneTimeAddressState(false);
                        const useOneTimeAddresId = billToState.value.shipTos?.find(shipTo => shipTo.oneTimeAddress)?.id;
                        updateCart({
                            shipToId: useOneTimeAddresId,
                        });
                        setShippingId(useOneTimeAddresId);
                        setSubmitted(false);
                    } else {
                        setHasErrors(true);
                    }
                }
            });
        }
    };

    const resetShippingAddress = () => {
        setAddress({
            ...shipToState.value,
        });
    };

    const toggleUseOneTimeAddress = (value: boolean) => {
        setSubmitted(false); // Stop validating address
        resetShippingAddressFormValidation(); // remove old address validation errors

        const useOneTimeAddresId = billToState.value.shipTos?.find(shipTo => shipTo.oneTimeAddress)?.id;
        setUseOneTimeAddressState(!useOneTimeAddressState);

        // if the user is a guest and the value is false, we should not call the OOTB setUseOneTimeAddress
        if ((!currentUserIsGuest || value)) {
            setUseOneTimeAddress({
                useOneTimeAddress: value,
                shipTo: value
                    ? billToState.value.shipTos?.find(shipTo => shipTo.oneTimeAddress) ?? shipToState.value
                    : undefined,
            } as any);
        }
        /**
         * STNRS-845
         * If the user updated the OTS address and then clicked on the cancel button,
         * we need to reset the redux store address to the original one same in cart.
        */
        if(!value) {
            resetShippingAddress();
        }
    };

    return (
        <CheckoutBox title={translate("Shipping & Billing Information")}>
            {hasOpenBid &&
                <Notify type="warning" showClose={false} {...styles.reviewShipToMessage}>
                    {siteMessage("STR_Review_ShipTo_Warning")}
                </Notify>
            }
            <StyledWrapper {...styles.container}>
                {useOneTimeAddressState && shipToAddressFields && (
                    <ShippingAddressForm countries={countries as CountryModel[]} fieldDisplay={shipToAddressFields} />
                )}
                {!useOneTimeAddressState && (
                    <ShippingAddressInfoDisplay
                        shipTo={shipToState.value}
                        onEdit={() => {}}
                        hideEdit={true}
                        extendedStyles={styles.shippingAddress}
                    />
                )}
                <BillingAddressInfoDisplay
                    billTo={billToState.value}
                    hideEdit={true}
                    onEdit={() => {}}
                    extendedStyles={styles.billingAddress}
                />
                {useOneTimeAddressState ? (
                    <StyledWrapper {...styles.actionsButtonContainer}>
                        <Button
                            {...styles.cancelAddingOneTimeAddressButton}
                            onClick={() => toggleUseOneTimeAddress(false)}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button {...styles.saveAddingOneTimeAddressButton} onClick={saveOneTimeAddress}>
                            {translate("Save")}
                        </Button>
                    </StyledWrapper>
                ) : (
                    <>
                        <StyledHr {...styles.styledHr} />
                        <StyledWrapper {...styles.changeShipToAddress}>
                            <Typography variant="h6">{translate("Change Ship To Address")}</Typography>
                            <Select
                                label={translate("Shipping Address")}
                                value={shippingId}
                                onChange={shippingAddressChangeHandler}
                                required={true}
                            >
                                {billToState.value.shipTos?.map(shipTo => (
                                        <option key={shipTo.id} value={shipTo.id}>
                                            {shipTo.label}
                                        </option>
                                    ))}
                            </Select>
                        </StyledWrapper>
                        <StyledWrapper {...styles.actionsContainer}>
                            <Typography {...styles.orTitle}>{translate("Or")}</Typography>
                            <Button {...styles.updateShipButton} onClick={() => toggleUseOneTimeAddress(true)}>
                                {translate("Update Ship To for This Order")}
                            </Button>
                        </StyledWrapper>
                    </>
                )}
            </StyledWrapper>
        </CheckoutBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShippingBillingInformation),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
